import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../../../firebase";
import "./QrMenuPage.css";
import Nav from "../../../components/Nav/Nav";
import { slugify } from "../../../utils/slugify";
import MenuSection from "./MenuSection";
import ImageModal from "./ImageModal";
import Skeleton from "./Skeleton";

/* ----- Main Component ----- */
export default function QrMenuPage() {
  const [menu, setMenu] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewItem, setPreviewItem] = useState(null);
  const [gridView, setGridView] = useState(true);
  const [activeCategory, setActiveCategory] = useState(null); // Նոր Layout State
  const [isTransitioning, setIsTransitioning] = useState(false); // Անցման Skeleton-ի համար

  const isSwitchingCategory = useRef(false);
  const [overscrollProgress, setOverscrollProgress] = useState(0); // Անիմացիայի համար
  const [overscrollDirection, setOverscrollDirection] = useState(null); // 'next' կամ 'prev'
  const [isHolding, setIsHolding] = useState(false); // Telegram-ի պես պահելու ինդիկատոր
  const holdTimerRef = useRef(null); // Պահելու ժամանակաչափը

  // Performance improvement: useMemo կանխում է ավելորդ ցիկլերը ամեն ռենդերի ժամանակ
  const categorySlugs = useMemo(() => {
    const categories = [...new Set(menu.map((s) => s.category))];
    return categories.map((cat) => ({
      name: cat,
      slug: slugify(cat),
    }));
  }, [menu]);

  // Performance: գտնում ենք միայն ակտիվ սեկցիան՝ առանց բոլորը render անելու
  const activeSection = useMemo(() => {
    if (!activeCategory) return null;
    return menu.find((section) => section.category === activeCategory);
  }, [menu, activeCategory]);

  const handleCloseModal = useCallback(() => {
    setPreviewItem(null);
  }, []);

  // --- Disable native Pull-to-Refresh ---
  useEffect(() => {
    document.body.style.overscrollBehaviorY = "none";
    return () => {
      document.body.style.overscrollBehaviorY = "auto";
    };
  }, []);

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const q = query(collection(db, "menu"), orderBy("order"));
        const snapshot = await getDocs(q);
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setMenu(data);
        // Բեռնումից հետո առաջին կատեգորիան դարձնում ենք ակտիվ (default)
        if (data.length > 0) {
          setActiveCategory(data[0].category);
        }
      } catch (error) {
        console.error("Չհաջողվեց բեռնել մենյուն։", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMenu();
  }, []);

  // --- Category Switch Loading State (Skeleton) ---
  useEffect(() => {
    if (loading || !activeSection) return;

    setIsTransitioning(true);

    // Գտնում ենք տվյալ բաժնի նկարների հասցեները
    const items = activeSection.items || [];
    const imageUrls = items.map((item) => item.imageUrl).filter(Boolean);
    // Վերցնում ենք միայն առաջին 3 նկարը՝ արագ բեռնման համար
    const imagesToPreload = imageUrls.slice(0, 3);

    // Եթե նկարներ չկան, ընդամենը փոքրիկ դադար ենք տալիս UI-ի սահուն անցման համար
    if (imagesToPreload.length === 0) {
      const timer = setTimeout(() => setIsTransitioning(false), 150);
      return () => clearTimeout(timer);
    }

    let isMounted = true;
    let loadedCount = 0;

    // Պահուստային (Fallback) ժամանակ. առավելագույնը սպասում ենք 1.5 վրկ,
    // որպեսզի վատ ինտերնետի դեպքում օգտատերը անվերջ չսպասի
    const maxWaitTimer = setTimeout(() => {
      if (isMounted) setIsTransitioning(false);
    }, 1500);

    // Ստուգում ենք ամեն մի նկարի բեռնումը
    const handleImageLoad = () => {
      loadedCount++;
      // Երբ առաջին 3 նկարները բեռնվեն կամ error տան, անջատում ենք Skeleton-ը
      if (loadedCount === imagesToPreload.length && isMounted) {
        clearTimeout(maxWaitTimer);
        setIsTransitioning(false);
      }
    };

    // Բրաուզերին հրահանգում ենք նախապես քաշել (preload) և ստուգել նկարները
    imagesToPreload.forEach((url) => {
      const img = new Image();
      img.src = url;
      img.onload = handleImageLoad;
      img.onerror = handleImageLoad; // Սխալի դեպքում էլ ենք հաշվում, որ չկախի
    });

    return () => {
      isMounted = false;
      clearTimeout(maxWaitTimer);
    };
  }, [activeSection, loading]);

  // --- Pull-to-Next & Prev Category (Overscroll) ---
  useEffect(() => {
    if (menu.length === 0 || !activeCategory) return;

    const currentIndex = menu.findIndex((s) => s.category === activeCategory);
    const hasNext = currentIndex !== -1 && currentIndex < menu.length - 1;
    const hasPrev = currentIndex !== -1 && currentIndex > 0;

    let overscrollAmount = 0;
    let startTouchY = 0;
    let isAtBottom = false;
    let isAtTop = false;
    let wheelTimeout;

    // --- Մաքրել պահելու ժամանակը ---
    const clearHold = () => {
      if (holdTimerRef.current) {
        clearTimeout(holdTimerRef.current);
        holdTimerRef.current = null;
      }
      setIsHolding(false);
    };

    // 1. Ստուգում ենք դիրքը (վերևում ենք, թե ներքևում)
    const checkPosition = () => {
      isAtBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 5;
      isAtTop = window.scrollY <= 5;
      if (!isAtBottom && !isAtTop) {
        overscrollAmount = 0; 
      }
    };

    // 2. Բուն անցման ֆունկցիան
    const triggerSwitch = (direction) => {
      if (isSwitchingCategory.current) return;

      if (direction === 'next' && hasNext) {
        isSwitchingCategory.current = true;
        setActiveCategory(menu[currentIndex + 1].category);
      } else if (direction === 'prev' && hasPrev) {
        isSwitchingCategory.current = true;
        setActiveCategory(menu[currentIndex - 1].category);
      } else {
        return;
      }

      // --- Haptic Feedback (Vibration) ---
      if (navigator.vibrate) {
        navigator.vibrate(50); // 50 միլիվայրկյան (շատ կարճ ու փափուկ) վիբրացիա
      }

      setOverscrollProgress(0);
      setOverscrollDirection(null);
      clearHold();

      // Ստիպողաբար անմիջապես բարձրացնում ենք վերև։ 
      // Overscroll-ի ժամանակ smooth-ը կարող է ընդհատվել, քանի որ մատը դեռ էկրանին է:
      window.scrollTo(0, 0);

      setTimeout(() => {
        isSwitchingCategory.current = false;
      }, 400); // Բլոկը պահում ենք մի փոքր երկար, որ էջը հասցնի վերև բարձրանալ
    };

    // 3. Desktop: Մկնիկի անիվ (Wheel) կամ Trackpad
    const handleWheel = (e) => {
      checkPosition();
      
      // Ներքև գնալիս
      if (isAtBottom && e.deltaY > 0 && hasNext) {
        overscrollAmount += e.deltaY;
        const progress = Math.min((overscrollAmount / 200) * 100, 100);
        setOverscrollProgress(progress);
        setOverscrollDirection('next');

        if (overscrollAmount > 200) { // Ավելի շատ վիրտուալ սքրոլ ենք պահանջում պահելու համար
          if (!holdTimerRef.current) {
            setIsHolding(true);
            holdTimerRef.current = setTimeout(() => {
              triggerSwitch('next');
              overscrollAmount = 0;
            }, 400); // 400ms պահելու ժամանակ
          }
        } else {
          clearHold();
        }
      // Վերև գնալիս
      } else if (isAtTop && e.deltaY < 0 && hasPrev) {
        overscrollAmount -= e.deltaY; // e.deltaY-ը բացասական է վերև սքրոլ անելիս, դրա համար հանում ենք (դրական դարձնելու համար)
        const progress = Math.min((overscrollAmount / 200) * 100, 100);
        setOverscrollProgress(progress);
        setOverscrollDirection('prev');

        if (overscrollAmount > 200) {
          if (!holdTimerRef.current) {
            setIsHolding(true);
            holdTimerRef.current = setTimeout(() => {
              triggerSwitch('prev');
              overscrollAmount = 0;
            }, 800);
          }
        } else {
          clearHold();
        }
      }

      clearTimeout(wheelTimeout);
      wheelTimeout = setTimeout(() => {
        overscrollAmount = 0;
        setOverscrollProgress(0);
        setOverscrollDirection(null);
        clearHold();
      }, 200);
    };

    // 4. Mobile: Մատով քաշել (Touch)
    const handleTouchStart = (e) => {
      checkPosition();
      startTouchY = e.touches[0].clientY;
    };

    const handleTouchMove = (e) => {
      checkPosition();
      const currentY = e.touches[0].clientY;
      const swipeDistance = startTouchY - currentY; // Դրական = վերև քաշել, Բացասական = ներքև քաշել
      
      // Կանխում ենք բրաուզերի ավտոմատ pull-to-refresh-ը վերևում, և rubber-banding էֆեկտը ներքևում
      if ((isAtTop && swipeDistance < 0) || (isAtBottom && swipeDistance > 0)) {
        if (e.cancelable) e.preventDefault();
      }

      if (isAtBottom && swipeDistance > 0 && hasNext) {
        const progress = Math.min((swipeDistance / 120) * 100, 100);
        setOverscrollProgress(progress);
        setOverscrollDirection('next');
        
        if (swipeDistance > 120) { // Պահանջում ենք 120px երկարությամբ ուժեղ քաշում
          if (!holdTimerRef.current) {
            setIsHolding(true);
            holdTimerRef.current = setTimeout(() => {
              triggerSwitch('next');
            }, 800); // Օգտատերը պետք է 800 միլիվայրկյան պահի մատը
          }
        } else {
          clearHold();
        }
      } else if (isAtTop && swipeDistance < 0 && hasPrev) {
        const absDistance = Math.abs(swipeDistance);
        const progress = Math.min((absDistance / 120) * 100, 100);
        setOverscrollProgress(progress);
        setOverscrollDirection('prev');
        
        if (absDistance > 120) {
          if (!holdTimerRef.current) {
            setIsHolding(true);
            holdTimerRef.current = setTimeout(() => {
              triggerSwitch('prev');
            }, 800);
          }
        } else {
          clearHold();
        }
      } else {
        setOverscrollProgress(0);
        setOverscrollDirection(null);
        clearHold();
        startTouchY = e.touches[0].clientY; // Եթե սահմաններում չենք, անընդհատ թարմացնում ենք մատի դիրքը
      }
    };

    const handleTouchEnd = () => {
      setOverscrollProgress(0); // Եթե մատը բաց թողեց մինչև 100%-ի հասնելը
      setOverscrollDirection(null);
      clearHold();
    };

    // Ավելացնում ենք Event Listener-ները
    window.addEventListener("scroll", checkPosition, { passive: true });
    window.addEventListener("wheel", handleWheel, { passive: true });
    window.addEventListener("touchstart", handleTouchStart, { passive: true });
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleTouchEnd, { passive: true });

    return () => {
      clearTimeout(wheelTimeout);
      clearHold();
      window.removeEventListener("scroll", checkPosition);
      window.removeEventListener("wheel", handleWheel);
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [activeCategory, menu]);

  // --- Navigation Hints Logic (UI/UX) ---
  const currentCatIndex = menu.findIndex((s) => s.category === activeCategory);
  const hasNextCat = currentCatIndex !== -1 && currentCatIndex < menu.length - 1;
  const hasPrevCat = currentCatIndex !== -1 && currentCatIndex > 0;
  const prevCatName = hasPrevCat ? menu[currentCatIndex - 1].category : null;
  const nextCatName = hasNextCat ? menu[currentCatIndex + 1].category : null;

  const handleSwitchCategory = (direction) => {
    if (isSwitchingCategory.current) return;
    isSwitchingCategory.current = true;
    
    if (direction === 'next' && hasNextCat) setActiveCategory(menu[currentCatIndex + 1].category);
    else if (direction === 'prev' && hasPrevCat) setActiveCategory(menu[currentCatIndex - 1].category);
    
    window.scrollTo(0, 0);
    setTimeout(() => {
      isSwitchingCategory.current = false;
    }, 400);
  };

  return (
    <div className="qr-menu-page layout-mode">
      <Nav 
        categories={categorySlugs} 
        gridView={gridView} 
        setGridView={setGridView} 
        activeCategory={activeCategory} // Փոխանցում ենք Nav-ին
        setActiveCategory={setActiveCategory} // Կառավարում ենք Nav-ից
      />

      <main className="menu-content-area">
        {loading || isTransitioning ? (
          <Skeleton gridView={gridView} count={6} />
        ) : menu.length === 0 ? (
          <p className="empty-state">
            Մենյուն դեռ դատարկ է։ Խնդրում ենք փորձել ավելի ուշ։
          </p>
        ) : activeSection ? (
          // Ռենդեր է լինում միայն մեկ MenuSection
          <div className="active-section-wrapper fade-in">
            {/* TOP HINT (Նախորդ բաժին) */}
            {hasPrevCat && (
              <div className="category-scroll-hint top-hint" onClick={() => handleSwitchCategory('prev')}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 19V5M5 12l7-7 7 7"/>
                </svg>
                <span>Նախորդը՝ {prevCatName}</span>
              </div>
            )}

            <MenuSection
              section={activeSection}
              index={0}
              onItemClick={setPreviewItem}
              gridView={gridView}
            />

            {/* BOTTOM HINT (Հաջորդ բաժին) */}
            {hasNextCat && (
              <div className="category-scroll-hint bottom-hint" onClick={() => handleSwitchCategory('next')}>
                <span>Հաջորդը՝ {nextCatName}</span>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 5v14M19 12l-7 7-7-7"/>
                </svg>
              </div>
            )}
          </div>
        ) : null}
      </main>

    {/* Overscroll Indicator Animation */}
    {overscrollProgress > 0 && overscrollDirection === 'next' && (
      <div 
        className={`overscroll-indicator next-indicator ${isHolding ? 'holding' : ''}`}
        style={{ 
          opacity: overscrollProgress / 100,
          transform: `translate(-50%, ${20 - (overscrollProgress / 100) * 20}px) ${isHolding ? 'scale(1.05)' : 'scale(1)'}` 
        }}
      >
        <span>{isHolding ? 'Պահեք...' : 'Հաջորդ բաժինը'}</span>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 5v14M19 12l-7 7-7-7"/>
        </svg>
        <div className="hold-progress-bar">
          <div className="hold-progress-fill"></div>
        </div>
      </div>
    )}

    {/* Overscroll Indicator Animation (Prev) */}
    {overscrollProgress > 0 && overscrollDirection === 'prev' && (
      <div 
        className={`overscroll-indicator prev-indicator ${isHolding ? 'holding' : ''}`}
        style={{ 
          opacity: overscrollProgress / 100,
          transform: `translate(-50%, ${-20 + (overscrollProgress / 100) * 20}px) ${isHolding ? 'scale(1.05)' : 'scale(1)'}` 
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 19V5M5 12l7-7 7 7"/>
        </svg>
        <span>{isHolding ? 'Պահեք...' : 'Նախորդ բաժինը'}</span>
        <div className="hold-progress-bar">
          <div className="hold-progress-fill"></div>
        </div>
      </div>
    )}

      {/* Modal */}
      <ImageModal 
        previewItem={previewItem} 
        onClose={handleCloseModal} 
      />
    </div>
  );
}